// Core packages
import { Component, Inject, OnDestroy, OnInit } from "@angular/core";
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from "@angular/material";

// Third party packages
import { CookieService } from "ngx-cookie-service";
import { Observable, of, Subscription, throwError } from "rxjs";
import { switchMap } from "rxjs/operators";

// Custom packages
import {
  DocumentArchiveDownloadDialogComponent,
  DocumentArchiveDownloadDialogData,
} from "src/app/modules/properties/components/document-archive-download-dialog/document-archive-download-dialog.component";
import { DocumentArchiveDocsService } from "src/app/modules/properties/document-archive-docs.service";
import {
  exceedsRemakePlantLimit,
  mappingArchiveDocRows,
  MappingArchiveDocRow,
  MAPPING_DOCS_PAGE_SIZE,
  MAX_REMAKE_PLANT_DOCS,
  nextMappingDocsRead,
  selectedMappingDocIds,
} from "src/app/modules/properties/mapping-archive-docs.utils";
import { DocumentArchiveDocListItem } from "src/app/shared/interfaces/document-archive-doc.interface";
import { ConfigService } from "src/app/shared/services/config.service";

/**
 * Which of the two legacy dialogs this one stands in for.
 *
 * @since 3.0.0
 */
export type MappingArchiveDocsDialogMode =
  /** «Rifare impianto»: a tick on every row, the ticked rows are carried over */
  | "remake"
  /** «Elimina impianto» / «Elimina la scheda»: the same list, to look at before confirming */
  | "delete";

/**
 * What the mapping tab passes in when opening the dialog.
 *
 * @since 3.0.0
 */
export interface MappingArchiveDocsDialogData {
  mode: MappingArchiveDocsDialogMode;
  /** Archive of the property (`property.documentArchive`) */
  documentArchiveId: string;
  /** The element whose documents are listed: its `structureId`, the `structureObjectId` of its rows */
  structureObjectId: string;
  /** True on a plant: the delete dialog says "no documents" for a plant only, as the legacy one */
  sectionPlant: boolean;
}

/**
 * What the dialog closes with on «Conferma»; nothing on «Annulla».
 *
 * @since 3.0.0
 */
export interface MappingArchiveDocsDialogResult {
  /** The rows ticked for «Rifare impianto», each once; always empty for the delete */
  documentArchiveDocIds: string[];
}

/** Where the list of the element stands */
type MappingDocsLoadState = "loading" | "loaded" | "failed";

/**
 * The documents of one mapping element on the 3.0.0 archive, before «Rifare impianto» and before
 * «Elimina impianto» / «Elimina la scheda» (MAP-A1).
 *
 * It stands in for the two legacy dialogs (`mapping-remake-dialog`, `mapping-delete-dialog`) once the
 * archive is switched on: same texts, same columns (Tipo documento, Descrizione, Data, «Scarica»),
 * same rows — the rule of `getDocsFromArchive`, defect included (@see mappingArchiveDocRows). What
 * changes is where the rows come from: `GET /document-archive-docs` for the element, every page of
 * it, read BEFORE the element is deleted, since the deletion withdraws them.
 *
 * Two cases the legacy dialogs never reached, because they crashed before opening:
 *  - the list cannot be read: «Rifare impianto» cannot be confirmed (without the list nothing can be
 *    chosen to carry over), the delete can (deleting loses no document);
 *  - more ticks than the back-end carries over: «Conferma» is off until some are removed, since with
 *    too many the back-end would copy nothing.
 *
 * «Scarica» opens the «Scarica i documenti» dialog of the new archive, which downloads through the
 * authenticated route of the row, never through a public path.
 *
 * Declared in MappingArchiveDocsDialogModule, imported by PropertiesModule: it is in the initial bundle
 * with the mapping tab that opens it.
 *
 * @since 3.0.0
 */
@Component({
  selector: "app-mapping-archive-docs-dialog",
  templateUrl: "./mapping-archive-docs-dialog.component.html",
})
export class MappingArchiveDocsDialogComponent implements OnInit, OnDestroy {
  private subscriptions: Subscription[] = [];

  /** Language of the session, for the names of the types */
  lang = "it";

  state: MappingDocsLoadState = "loading";

  rows: MappingArchiveDocRow[] = [];

  /** The limit, for the message that names it */
  readonly maxRemakeDocs = MAX_REMAKE_PLANT_DOCS;

  /**
   * Class constructor
   */
  constructor(
    public dialogRef: MatDialogRef<MappingArchiveDocsDialogComponent, MappingArchiveDocsDialogResult>,
    @Inject(MAT_DIALOG_DATA) public data: MappingArchiveDocsDialogData,
    private dialog: MatDialog,
    private configService: ConfigService,
    private cookieService: CookieService,
    private documentArchiveDocsService: DocumentArchiveDocsService
  ) {}

  /**
   * Read the documents of the element.
   *
   * Without both ids nothing is asked: a listing without `structureObjectId` would be the WHOLE
   * archive, offered as the documents of this element.
   *
   * @since 3.0.0
   */
  ngOnInit(): void {
    this.lang = this.cookieService.get("lang") || this.configService.defaultLang;

    const archiveId = this.data && typeof this.data.documentArchiveId === "string" ? this.data.documentArchiveId : "";
    const elementId = this.data && typeof this.data.structureObjectId === "string" ? this.data.structureObjectId : "";
    if (!archiveId || !elementId) {
      this.state = "failed";
      return;
    }

    this.subscriptions.push(
      this.readElement(archiveId, elementId, 0, [], 1).subscribe(
        (items) => {
          this.rows = mappingArchiveDocRows(items, this.lang);
          this.state = "loaded";
        },
        () => {
          // The dialog says it in its body; a toast on top of it would only repeat it
          this.rows = [];
          this.state = "failed";
        }
      )
    );
  }

  /**
   * Handle component destroy
   *
   * @since 3.0.0
   */
  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  /** True on «Rifare impianto» */
  get isRemake(): boolean {
    return !!this.data && this.data.mode === "remake";
  }

  /** True when more rows are ticked than the back-end carries over */
  get tooManySelected(): boolean {
    return this.isRemake && exceedsRemakePlantLimit(selectedMappingDocIds(this.rows).length);
  }

  /**
   * The "no documents" line of the legacy dialogs: always on «Rifare impianto», on a plant only for
   * the delete. Not when the list failed: that says something else.
   */
  get showNoDocs(): boolean {
    return this.state === "loaded" && this.rows.length === 0 && (this.isRemake || !!this.data.sectionPlant);
  }

  /** Whether «Conferma» is on (@see MappingArchiveDocsDialogComponent for the two failure cases) */
  get canConfirm(): boolean {
    if (this.state === "loading") {
      return false;
    }
    if (this.isRemake) {
      return this.state === "loaded" && !this.tooManySelected;
    }
    return true;
  }

  /** The text of the legacy dialog this one stands in for */
  get messageKey(): string {
    return this.isRemake
      ? "properties.structures.textConfimFormRemake"
      : "properties.structures.textConfimFormDeletePlant";
  }

  /**
   * Open the files of a row in the «Scarica i documenti» dialog of the new archive.
   *
   * @since 3.0.0
   *
   * @param row The row whose «Scarica» was clicked
   */
  onDownload(row: MappingArchiveDocRow): void {
    if (!row || !row.docId || !Array.isArray(row.files) || !row.files.length) {
      return;
    }
    const dialogData: DocumentArchiveDownloadDialogData = {
      docId: row.docId,
      files: row.files,
      barCode: row.barCode,
    };
    this.dialog.open(DocumentArchiveDownloadDialogComponent, {
      data: dialogData,
      width: "600px",
    });
  }

  /**
   * Handle dialog confirmation
   *
   * @since 3.0.0
   */
  onConfirmation(): void {
    if (!this.canConfirm) {
      return;
    }
    const result: MappingArchiveDocsDialogResult = {
      documentArchiveDocIds: this.isRemake ? selectedMappingDocIds(this.rows) : [],
    };
    this.dialogRef.close(result);
  }

  /**
   * Handle dialog denial
   *
   * @since 3.0.0
   */
  onDenial(): void {
    this.dialogRef.close();
  }

  /**
   * Every row of the element, page after page (@see nextMappingDocsRead for when it stops).
   *
   * An answer without its rows and a list too long to be read whole are errors, not short lists: a
   * short list would hide documents from the remake.
   */
  private readElement(
    archiveId: string,
    elementId: string,
    skip: number,
    readSoFar: DocumentArchiveDocListItem[],
    page: number
  ): Observable<DocumentArchiveDocListItem[]> {
    return this.documentArchiveDocsService
      .list({
        documentArchiveId: archiveId,
        structureObjectId: elementId,
        skip,
        limit: MAPPING_DOCS_PAGE_SIZE,
      })
      .pipe(
        switchMap((res) => {
          if (!res || !res.status || !res.data || !Array.isArray(res.data.items)) {
            return throwError(new Error("The listing of the element came back without its rows"));
          }
          const items = readSoFar.concat(res.data.items);
          const total = typeof res.data.totalCount === "number" ? res.data.totalCount : items.length;
          const next = nextMappingDocsRead(items.length, res.data.items.length, total, page);
          if (next.kind === "next") {
            return this.readElement(archiveId, elementId, next.skip, items, page + 1);
          }
          if (next.kind === "tooMany") {
            return throwError(new Error("The listing of the element is too long to be read whole"));
          }
          return of(items);
        })
      );
  }
}
